// Implement Raptor API

import type { UserAuth } from '@repo/common';
import type { RaptorResponse } from '@repo/common/types/Raptor';
import constants from '@repo/sharedlib/src/utils/constants';
import { isAxiosError } from '@repo/toolslib/src/utils/catching.util';
import { getPrivateClient } from './common.data';

export const raptorSearch = async (
  query: string,
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>
): Promise<RaptorResponse | null> => {
  try {
    console.log('[raptorSearch] Calling /search endpoint with query:', query);
    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_SIZE['3xl'] });
    const response = await client.post('/search', { query });
    console.log('[raptorSearch] Received response:', {
      status: response.status,
      hasData: !!response.data,
      dataKeys: response.data ? Object.keys(response.data) : []
    });
    return response.data;
  } catch (error) {
    console.error('[raptorSearch] Error searching with Raptor:', error);

    // Check if the error is due to service unavailability, timeout, or no index found
    if (isAxiosError(error)) {
      const status = error.response?.status;
      const data = error.response?.data;

      // Handle service unavailability (503) and timeouts (504)
      if (status === 503 || status === 504) {
        return {
          response: '',
          error: 'service_unavailable',
          source_nodes: [],
          metadata: {
            response_mode: 'compact',
            mode: 'search',
            error: 'service_unavailable',
            message: data?.message || 'RAG service is currently unavailable or timed out'
          }
        };
      }

      // Handle 404 errors - check for specific error codes
      if (status === 404) {
        // Check for NO_DATA_INDEXED specifically
        if (data?.error_code === 'NO_DATA_INDEXED') {
          return {
            response: '',
            error: 'no_index',
            source_nodes: [],
            metadata: {
              response_mode: 'compact',
              mode: 'search',
              error: 'no_index',
              message:
                data?.detail ||
                data?.message ||
                'Your knowledge base is empty. Please index documents first.'
            }
          };
        }

        // Check for COLLECTION_NOT_FOUND
        if (data?.error_code === 'COLLECTION_NOT_FOUND') {
          return {
            response: '',
            error: 'collection_not_found',
            source_nodes: [],
            metadata: {
              response_mode: 'compact',
              mode: 'search',
              error: 'collection_not_found',
              message: 'Collection not found'
            }
          };
        }

        // Generic 404 fallback - treat as no index
        return {
          response: '',
          error: 'no_index',
          source_nodes: [],
          metadata: {
            response_mode: 'compact',
            mode: 'search',
            error: 'no_index',
            message: data?.detail || data?.message || 'No knowledge base found for this user.'
          }
        };
      }

      // Handle 422 Unprocessable Entity - typically validation errors
      if (status === 422) {
        // Check if it's specifically about no data
        if (data?.error_code === 'NO_DATA_INDEXED') {
          return {
            response: '',
            error: 'no_index',
            source_nodes: [],
            metadata: {
              response_mode: 'compact',
              mode: 'search',
              error: 'no_index',
              message:
                data?.detail ||
                data?.message ||
                'Your knowledge base is empty. Please index documents first.'
            }
          };
        }

        // Generic 422 - validation error
        return {
          response: '',
          error: 'validation_error',
          source_nodes: [],
          metadata: {
            response_mode: 'compact',
            mode: 'search',
            error: 'validation_error',
            message: data?.detail || data?.message || 'Invalid request parameters'
          }
        };
      }

      // Handle 500 errors - check for DATABASE_ERROR
      if (status === 500 && data?.error_code === 'DATABASE_ERROR') {
        return {
          response: '',
          error: 'service_unavailable',
          source_nodes: [],
          metadata: {
            response_mode: 'compact',
            mode: 'search',
            error: 'service_unavailable',
            message: 'Vector database issue'
          }
        };
      }
    }

    return null;
  }
};

/**  * Response from RAG indexing operation  */
export interface RagIndexingResponse {
  user_id: string;
  document_count: number;
  task_id?: string;
  status: 'success' | 'running' | 'error';
  error_message?: string;
}

/**  * Index a transcript in the RAG system using append mode.  * This adds the transcript to the existing index without replacing other documents.  *  * @param auth - User authentication tokens (idToken and refreshToken)  * @param transcriptId - The transcript ID to index  * @returns The indexing response or null if failed  */
export const indexTranscriptInRag = async (
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  transcriptId: string
): Promise<RagIndexingResponse | null> => {
  try {
    console.log(`[RAG] Indexing transcript ${transcriptId} in append mode`);

    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_XL });
    const response = await client.post<RagIndexingResponse>('/index-transcript', {
      transcriptId
    });

    console.log(`[RAG] Indexing response for ${transcriptId}:`, response.data);
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      console.error('Error indexing transcript:', error.message);
      return {
        user_id: '',
        document_count: 0,
        status: 'error',
        error_message: error.message
      };
    } else if (error instanceof Error) {
      console.error(`[RAG] Error indexing transcript ${transcriptId}:`, error.message);
      return {
        user_id: '',
        document_count: 0,
        status: 'error',
        error_message: error.message
      };
    }
    return {
      user_id: '',
      document_count: 0,
      status: 'error',
      error_message: 'Unknown error occurred'
    };
  }
};

/**  * Check if a transcript exists in the RAG index  *  * @param auth - User authentication tokens (idToken and refreshToken)  * @param transcriptId - The transcript ID to check  * @returns Object with exists flag and document count  */
export const checkTranscriptIndexed = async (
  auth: Pick<UserAuth, 'idToken' | 'refreshToken'>,
  transcriptId: string
): Promise<{ exists: boolean; documentCount: number }> => {
  try {
    const client = getPrivateClient(auth, { timeout: constants.TIMEOUT_XL });
    const response = await client.get(`/index-exists/${transcriptId}`);

    return {
      exists: response.data.exists,
      documentCount: response.data.document_count || 0
    };
  } catch (error) {
    if (isAxiosError(error)) {
      console.error('Error checking transcript:', error.message);
      return {
        exists: false,
        documentCount: 0
      };
    } else if (error instanceof Error) {
      console.error(`[RAG] Error checking transcript ${transcriptId}:`, error.message);
      return {
        exists: false,
        documentCount: 0
      };
    }
    console.warn(`[RAG] Error checking transcript ${transcriptId}:`, error);
    return {
      exists: false,
      documentCount: 0
    };
  }
};
